import { Link } from "react-router-dom";
import { ISLANDS } from "../lib/constants";
import { useT } from "../lib/LangContext";
import { useAppVersion } from "../lib/useAppVersion";
import { useIsSubscribed } from "../lib/useIsSubscribed";
import { openSubscribe } from "./SubscribeModalHost";
import { DigestLaunchNotice } from "./DigestLaunchNotice";

const LEGAL_EMAIL = "legal@islandsdaily.com";
const CONTACT_EMAIL = "hello@islandsdaily.com";

export const Footer = ({ onSubscribe }) => {
  const { t, tIsland } = useT();
  const appVersion = useAppVersion();
  const isSubscribed = useIsSubscribed();
  // Fall back to a default subscribe handler when the parent page didn't
  // wire one in. Prevents dead clicks on ArticlePage / ObituariesPage /
  // PapiamentoPage / AboutPage where <Footer /> is mounted without an
  // onSubscribe prop (fix 2026-02-22 per Calvin).
  const handleSubscribe = onSubscribe || (() => openSubscribe("footer_default"));
  return (
    <footer
      className="bg-white border-t border-[#EEEEEE] mt-16"
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div>
          <div className="font-serif-display text-2xl mb-3">
            <span className="text-[#1A1A1A]">Islands</span>
            <span className="text-[#0AADA0]">Daily</span>
          </div>
          <p className="text-sm text-[#6B7280] leading-relaxed">
            {t("about_footer_desc")}
          </p>
        </div>

        <div>
          <h4 className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-4">
            {t("all_islands")}
          </h4>
          <ul className="space-y-2">
            {ISLANDS.filter((i) => i.slug !== "all").map((i) => {
              // Both the plain-island route (`/:islandSlug`) and the
              // per-island cruise-schedule route (`/:islandSlug/cruise-
              // schedule`) accept the internal slug directly — no dash
              // translation needed. CruiseSchedulePage's URL_TO_SLUG
              // map accepts both `stmaarten` and legacy `st-maarten`
              // for backward compat with older inbound links.
              const urlSlug = i.slug;
              return (
                <li
                  key={i.slug}
                  className="text-sm text-[#1A1A1A] flex items-center gap-2 flex-wrap"
                >
                  <span>{i.flag}</span>
                  <Link
                    to={`/${urlSlug}`}
                    className="hover:text-[#0AADA0]"
                    data-testid={`footer-island-link-${i.slug}`}
                  >
                    {tIsland(i.slug)}
                  </Link>
                  <span className="text-[#D1D5DB]">·</span>
                  <Link
                    to={`/${urlSlug}/cruise-schedule`}
                    className="text-[12px] text-[#6B7280] hover:text-[#0AADA0]"
                    data-testid={`footer-cruise-link-${i.slug}`}
                  >
                    🚢 {t("footer_cruise_schedule")}
                  </Link>
                  <span className="text-[#D1D5DB]">·</span>
                  <Link
                    to={`/${urlSlug}/carnival`}
                    className="text-[12px] text-[#6B7280] hover:text-[#0AADA0]"
                    data-testid={`footer-carnival-link-${i.slug}`}
                  >
                    🎭 {t("footer_carnival")}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>

        {!isSubscribed && (
        <div>
          <h4 className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-4">
            {t("stay_informed")}
          </h4>
          <p className="text-sm text-[#6B7280] mb-4 leading-relaxed">
            {t("islands_footer_desc")}
          </p>
          <DigestLaunchNotice
            className="mb-4"
            testid="footer-launch-notice"
          />
          <button
            onClick={handleSubscribe}
            className="px-5 py-2 rounded-full bg-[#E63946] hover:bg-[#cf2f3c] text-white text-sm transition-colors"
            data-testid="footer-subscribe-btn"
          >
            {t("subscribe")}
          </button>
        </div>
        )}
      </div>

      <div className="border-t border-[#EEEEEE] py-6 text-center px-4">
        <p className="font-serif-display italic text-[#0AADA0] text-lg">
          {t("tagline")}
        </p>
        <p
          className="mt-1 text-[12px] text-[#6B7C82]"
          data-testid="footer-islands-line"
        >
          Aruba · Curaçao · Bonaire · St. Maarten
        </p>
      </div>

      {/* Trilingual content-owner contact strip — always visible, never
          language-switched, on every page */}
      <div
        className="bg-[#1A2E35] py-6 px-4 text-center"
        data-testid="content-owner-strip"
      >
        <div className="border-t border-white/10 pt-5">
          <div
            className="space-y-1 text-[11px] text-[#6B7C82]"
            data-testid="content-owner-lines"
          >
            <p>
              Content owners &amp; press:{" "}
              <a
                href={`mailto:${LEGAL_EMAIL}`}
                className="text-[#0AADA0] hover:underline"
                data-testid="footer-contact-link"
              >
                {LEGAL_EMAIL}
              </a>
            </p>
            <p>
              Inhoudseigenaren &amp; pers:{" "}
              <a
                href={`mailto:${LEGAL_EMAIL}`}
                className="text-[#0AADA0] hover:underline"
                data-testid="footer-contact-link-nl"
              >
                {LEGAL_EMAIL}
              </a>
            </p>
            <p>
              Propietarios de contenido &amp; prensa:{" "}
              <a
                href={`mailto:${LEGAL_EMAIL}`}
                className="text-[#0AADA0] hover:underline"
                data-testid="footer-contact-link-es"
              >
                {LEGAL_EMAIL}
              </a>
            </p>
          </div>
          <p
            className="mt-3 text-[10px] text-[#6B7C82]"
            data-testid="footer-removal-sla"
          >
            Removal requests are processed within 48 hours · Verzoeken worden
            binnen 48 uur verwerkt · Las solicitudes se procesan en 48 horas
          </p>
          <div
            className="mt-4 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[12px] text-[#6B7C82]"
            data-testid="footer-links"
          >
            <Link
              to="/papiamento"
              className="hover:text-[#0AADA0]"
              data-testid="footer-papiamento-link"
            >
              {t("nav_papiamento_hub")}
            </Link>
            <span className="text-[#9CA3AF]">·</span>
            <Link
              to="/terms"
              className="hover:text-[#0AADA0]"
              data-testid="footer-terms-link"
            >
              Terms &amp; Copyright
            </Link>
            <span className="text-[#9CA3AF]">·</span>
            <Link
              to="/privacy"
              className="hover:text-[#0AADA0]"
              data-testid="footer-privacy-link"
            >
              Privacy
            </Link>
            <span className="text-[#9CA3AF]">·</span>
            <Link to="/about" className="hover:text-[#0AADA0]" data-testid="footer-about-link">
              About
            </Link>
            <span className="text-[#9CA3AF]">·</span>
            <Link to="/sponsor" className="hover:text-[#0AADA0]" data-testid="footer-sponsor-link">
              Advertise
            </Link>
            <span className="text-[#9CA3AF]">·</span>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="hover:text-[#0AADA0]"
              data-testid="footer-contact-page-link"
            >
              Contact
            </a>
          </div>
          <p
            className="text-[11px] text-[#6B7C82] mt-3"
            data-testid="footer-copyright"
          >
            © {new Date().getFullYear()} IslandsDaily. All rights reserved.
          </p>
          {appVersion ? (
            <p
              className="text-[10px] text-[#4A5A5F] mt-2 font-mono tracking-tight"
              data-testid="footer-app-version"
            >
              {appVersion}
            </p>
          ) : null}
        </div>
      </div>
    </footer>
  );
};
